import { BulkEntityValidationRecord } from 'src/components/selfServeWorkflows/requirementsPortal/bulkUploadSteps/BulkExecutionStepProgress';
import {
	AutoPopulationAttributeValue,
	AutoPopulationMapping,
	fetchAutoPopulationMapping,
	hasAutoPopulationDefinitions,
	initiateAutoPopulation,
} from 'src/utils/autoPopulationService';
import { logger } from 'src/utils/logger';

export type { AutoPopulationAttributeValue };

const toAttributeValues = (inputRecord: Record<string, unknown>):
	Record<string, AutoPopulationAttributeValue> => {
	const attributeValues: Record<string, AutoPopulationAttributeValue> = {};

	for (const [attributeId, attributeValue] of Object.entries(inputRecord || {})) {
		if (attributeValue == null || attributeValue === '') continue;

		if (Array.isArray(attributeValue)) attributeValues[attributeId] = { listValue: attributeValue as string[] };
		else if (typeof attributeValue === 'number') attributeValues[attributeId] = { numberValue: attributeValue };
		else if (typeof attributeValue === 'boolean') attributeValues[attributeId] = { booleanValue: attributeValue };
		else attributeValues[attributeId] = { stringValue: String(attributeValue) };
	}

	return attributeValues;
};

export const autoPopulateRow = async (
	inputRecord: Record<string, unknown>,
	workflowId: string,
): Promise<Record<string, unknown>> => {
	const entityName = (inputRecord?.EntityName as string) || '<unknown>';
	const attributeValues = toAttributeValues(inputRecord);

	logger.info(`[AutoPopulation] executing row entity=${entityName}, inputAttrs=${Object.keys(attributeValues).length}`);

	try {
		const response = await initiateAutoPopulation({
			workflowId,
			attributeUpdated: 'EntityName',
			attributeValues,
		});

		const populatedAttributes: Record<string, unknown> = {};

		for (const [attributeId, typedValue] of Object.entries(response.populatedAttributes)) {
			const value = typedValue.stringValue ?? typedValue.numberValue ?? typedValue.booleanValue ?? typedValue.listValue;

			if (value != null) populatedAttributes[attributeId] = value;
		}

		logger.info(`[AutoPopulation] row success entity=${entityName}, populatedKeys=${Object.keys(populatedAttributes).length}`);

		return populatedAttributes;
	} catch (err) {
		logger.error(`AutoPopulation failed for row entity=${entityName}`, err as Error);

		return {};
	}
};

const AUTO_POPULATION_BATCH_SIZE = 5;

export const autoPopulateRows = async (
	records: BulkEntityValidationRecord[],
	workflowId: string,
): Promise<BulkEntityValidationRecord[]> => {
	await logger.info(`[AutoPopulation] begin enrichment -> workflowId=${workflowId}, rowCount=${records.length}, batchSize=${AUTO_POPULATION_BATCH_SIZE}`);

	let mapping: AutoPopulationMapping;

	try {
		mapping = await fetchAutoPopulationMapping(workflowId);
	} catch (err) {
		await logger.error(`[AutoPopulation] definition lookup failed, skipping enrichment -> workflowId=${workflowId}`, err as Error);

		return records;
	}

	if (!hasAutoPopulationDefinitions(mapping)) {
		await logger.info(`[AutoPopulation] skipping enrichment -> no definition for workflowId=${workflowId}`);

		return records;
	}

	const enrichedRecords: BulkEntityValidationRecord[] = [];

	for (let batchStart = 0; batchStart < records.length; batchStart += AUTO_POPULATION_BATCH_SIZE) {
		const batch = records.slice(batchStart, batchStart + AUTO_POPULATION_BATCH_SIZE);

		await logger.info(`[AutoPopulation] processing batch -> start=${batchStart}, size=${batch.length}`);

		const enrichedBatch = await Promise.all(
			batch.map(async (record) => {
				if (record.autoPopulated) {
					await logger.info(`[AutoPopulation] skip row entity=${record.entityId}, already autoPopulated`);

					return record;
				}

				const populatedAttributes = await autoPopulateRow(record.inputRecord, workflowId);

				const mergedInputRecord: Record<string, unknown> = { ...populatedAttributes };

				for (const [attributeId, value] of Object.entries(record.inputRecord)) {
					if (value != null && value !== '') mergedInputRecord[attributeId] = value;
				}

				return {
					...record,
					inputRecord: mergedInputRecord,
					autoPopulationResults: populatedAttributes,
					autoPopulated: true,
				};
			}),
		);

		enrichedRecords.push(...enrichedBatch);
	}

	const failedCount = enrichedRecords.filter(
		(r) => r.autoPopulated && Object.keys(r.autoPopulationResults || {}).length === 0,
	).length;

	if (failedCount > 0) {
		await logger.warn(`[AutoPopulation] ${failedCount}/${records.length} rows returned empty results`);
	}

	await logger.info(`[AutoPopulation] enrichment done -> total=${enrichedRecords.length}`);

	return enrichedRecords;
};